// HU-19 · Completar el Formato Amarillo (F-05, Sprint 2; rev. 14, 9 criterios). Dato L2.
//
// PRE-CARGAR LO QUE EXISTE, PEDIR LO QUE FALTA (RN-33). Las 14 secciones llegan armadas del
// servidor (`GET …/formato-amarillo/documento`): lo que el Formato Verde ya capturó aparece
// lleno y SIN marca (C1); solo lo que falta lleva «A completar» (C2). Cada campo dice de
// dónde viene y si se llena aquí:
//
//   · `editable_aqui` → campo propio del trámite (tipo de informe, notificante, acción tras
//     el evento, criterio del veterinario, exposición previa, reexposición): se guarda en
//     el Formato Amarillo y no modifica el Formato Verde (C5).
//   · «Fecha de término del evento» (§8) se pide AQUÍ pero se captura en el CASO
//     (`PUT /casos/{id}/evento-adverso/fecha-termino`): es el único campo del Verde que el
//     Amarillo exige y el Verde no pide (C3).
//   · «Serie» (§4), «Vía y lugar» y «Según rótulo» (§6) y «Seguimiento cronológico» (§8)
//     también se completan AQUÍ y se guardan en el CASO
//     (`PATCH /casos/{id}/datos-del-formato-amarillo`): el Verde no los captura (O-08).
//   · lo demás se corrige en el caso o en la evaluación (§12 nombra el algoritmo, C7).
//
// «Generar documento» (C4) nace «Formato Amarillo v1» con fecha, hora y usuario; corregir y
// volver a generar es la v2 y la v1 sigue igual (C6). «Imprimir» abre SOLO el documento
// (C4). Sin permiso L2 sobre un caso que afecta a personas, el servidor responde 403 y aquí
// se muestra el motivo (C8). Un caso no grave no llega aquí: no tiene trámite (C9).

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, ArrowLeft, CheckCircle2, FileCheck2, FolderOpen, History, Lock, Pencil, Save } from 'lucide-react';
import { Alert, Button, Card, Field, Input, L2Tag, Modal, Mono, PageHeader, Select, StatusBadge, Textarea } from '../components/ui';
import Bandera from '../components/Banderas';
import {
  completarDatosDelFormatoAmarillo, etiquetaDeLaCarta, fijarFechaDeTerminoDelEvento, generarFormatoAmarillo, guardarFormatoAmarillo, imprimirFormatoAmarillo, topeDelFormato,
  verFormatoAmarillo,
} from '../api/regulatorio';
import { mensajeDeTopeSuperado } from '../api/evaluacion';
import { imprimirFormatoAmarilloParaLaAutoridad } from '../api/cartas';
import type { CampoDelFormato, DatosDelCasoParaElFormato, DatosDelFormatoAmarillo, FormatoAmarillo as Formato, VersionDelFormato } from '../api/regulatorio';
import { listarCatalogo } from '../api/catalogos';
import type { RespuestaDeCatalogo } from '../api/catalogos';
import { comoFecha, comoFechaDeInstante, comoFechaYHora } from '../lib/fechas';
import { useZonaDelPais } from '../lib/CatalogosContexto';
import { aFallo } from '../api/cliente';
import type { Fallo } from '../api/cliente';
import { abrirDocumentoEnPestana } from '../lib/abrirDocumento';
import BotonVerEImprimir from '../components/BotonVerEImprimir';

export const MARCA_A_COMPLETAR = 'A completar';
const CLAVE_FECHA_TERMINO = 'evento_adverso.fecha_termino';
// Los siete campos del Formato Amarillo que son una FECHA (QA, 23-sep-2026).
//
// La lista es cerrada y va por CLAVE, no por el aspecto del valor. Reconocer una fecha
// «porque parece una fecha» reformatearía un texto libre que casualmente tuviera esa
// forma —«Referencia del informe inicial» es el candidato obvio— y nadie lo habría
// pedido. Las claves salen de `formato_amarillo_documento.py`, donde el backend las
// colapsa a `AAAA-MM-DD` con `.isoformat()`: ahí SE SABE que el valor era una fecha, y
// esa información se pierde al construir el DTO, que no lleva tipo.
//
// Si el backend algún día declara el tipo del campo, esta lista sobra y mejor.
const CLAVES_DE_FECHA = new Set([
  'fecha_recepcion',            // §1, del Formato Verde
  'fecha_informe_inicial',      // §2, del trámite
  'medicamento.vencimiento',    // §4
  'tratamiento.inicio',         // §6
  'tratamiento.fin',            // §6
  'evento.fecha_inicio',        // §8
  CLAVE_FECHA_TERMINO,          // §8
]);
/**
 * `animal.2.especie` -> `animal.especie`. Desde el segundo bloque de una sección repetida
 * (§4, §5, §6) la clave lleva el número del bloque; las listas de arriba están escritas
 * con la clave base, así que sin esto la fecha de vencimiento del segundo producto se
 * mostraría como `2026-03-14` mientras la del primero se ve `14/03/2026`.
 * Misma convención que `claves_del_formato.py` en el backend.
 */
const claveBase = (clave: string): string => {
  const partes = clave.split('.');
  return partes.length === 3 && /^\d+$/.test(partes[1]) ? `${partes[0]}.${partes[2]}` : clave;
};

const CAMPOS_BOOLEANOS = new Set(['exposicion_previa', 'reaccion_previa', 'desaparecio_al_suspender', 'reaparecio_al_readministrar']);
const CATALOGO_DE = { tipo_informe: 'tipo_informe_regulatorio', criterio_veterinario: 'criterio_veterinario', accion_tras_evento: 'accion_tras_evento' } as const;
const SI_NO = [{ value: 'si', label: 'Sí' }, { value: 'no', label: 'No' }];

type Opcion = { value: string; label: string };
const ORIGEN: Record<string, string> = { formato_verde: 'Formato Verde', tramite: 'Trámite', evaluacion: 'Evaluación del Comité', sistema: 'Sistema' };

// DD/MM/AAAA EN TODA LA INTERFAZ (25-sep-2026). Antes recortaba el ISO y lo mostraba
// tal cual, asi que en la misma fila convivian «2026-09-24» y «25/09/26». RN-82: el día se
// toma en la zona del país del caso.
const fecha = (iso: unknown, zona: string | null): string => comoFechaDeInstante(iso, zona) || '—';

/** Las opciones de un desplegable a partir del catálogo: solo los valores activos. */
const opcionesDelCatalogo = (valores: RespuestaDeCatalogo['valores']): Opcion[] =>
  valores.filter((v) => v.activo !== false).map((v) => ({ value: v.codigo, label: v.nombre ?? v.codigo }));

/** El «Sí»/«No» que trae el servidor y el 'si'/'no' del desplegable son el mismo dato. */
const aOpcionSiNo = (valor: string): string => {
  if (valor === 'Sí') return 'si';
  if (valor === 'No') return 'no';
  return valor;
};

// Por qué no se abre: sin permiso L2 (403) se explica el motivo y queda la auditoría (C8);
// un caso sin trámite (404) no tiene documento (C9); lo demás es un fallo del servidor.
const tituloDelFalloAlAbrir = (estado: number | undefined): string => {
  if (estado === 403) return 'El documento no se abre sin permiso L2';
  if (estado === 404) return 'Este caso no tiene Formato Amarillo';
  return 'No se pudo abrir el Formato Amarillo';
};

function FalloAlAbrir({ fallo }: { fallo: Fallo }) {
  const sinPermiso = fallo.estado === 403;
  return (
    <Alert tone={sinPermiso ? 'warning' : 'danger'} title={tituloDelFalloAlAbrir(fallo.estado)} detalleTecnico={fallo.detalleTecnico}>
      {sinPermiso ? 'La sección 11 (afectación a personas) es dato de salud. El intento quedó registrado en la auditoría.' : fallo.message}
    </Alert>
  );
}

function Valor({ campo }: { campo: CampoDelFormato }) {
  if (campo.a_completar) {
    return <StatusBadge tone="warning" icon={AlertTriangle}>{MARCA_A_COMPLETAR}</StatusBadge>;
  }
  // Solo la PRESENTACIÓN. `campo.valor` sigue intacto, y eso importa: la línea que
  // ofrece «Cambiar» la fecha de término copia ese mismo valor al control `type="date"`,
  // que solo acepta ISO. Formatear el dato dejaría el control vacío y el paso 11 del
  // CP-19-01 moriría en silencio.
  // O-08 (OBS-3): la etiqueta del catálogo cuando el servidor la da; nunca el código crudo.
  const visible = CLAVES_DE_FECHA.has(claveBase(campo.clave)) ? comoFecha(campo.valor) : (campo.valor_legible ?? campo.valor);
  return <span className="text-meta whitespace-pre-wrap">{visible || <span className="text-ink-soft">—</span>}</span>;
}

/** El control de un campo propio del trámite (C5): catálogo, Sí/No o texto/fecha según la clave. */
function EntradaPropia({ campo, valor, opciones, tope, onCambiar }: Readonly<{
  campo: CampoDelFormato;
  valor: string;
  opciones: Opcion[];
  /** Tope de caracteres del texto libre (parámetro); sin él no hay maxLength. */
  tope?: number;
  onCambiar: (valor: string) => void;
}>) {
  if (CATALOGO_DE[campo.clave as keyof typeof CATALOGO_DE]) {
    return (
      <Select aria-label={campo.etiqueta} placeholder="Elegir…" value={valor}
        onChange={(e) => onCambiar(String(e.target.value ?? ''))}
        options={opciones} />
    );
  }
  if (CAMPOS_BOOLEANOS.has(campo.clave)) {
    return (
      <Select aria-label={campo.etiqueta} placeholder="Sin dato" value={aOpcionSiNo(valor)}
        onChange={(e) => onCambiar(String(e.target.value ?? ''))}
        options={SI_NO} />
    );
  }
  return (
    <Input aria-label={campo.etiqueta} type={campo.clave.startsWith('fecha') ? 'date' : 'text'} value={valor} maxLength={tope}
      onChange={(e) => onCambiar(e.target.value)} />
  );
}

/** El «n / 200 caracteres» de un texto con tope; nada si no lo tiene. */
const cuentaAtras = (valor: string, tope?: number): string | undefined => (tope ? `${valor.length} / ${tope} caracteres` : undefined);

/** Los campos propios del trámite que llegan con su valor, como los deja `cargar`. */
const propiosDe = (f: Formato): Record<string, string> => {
  const propios: Record<string, string> = {};
  for (const s of f.secciones) for (const c of s.campos) if (c.editable_aqui) propios[c.clave] = c.valor ?? '';
  return propios;
};

/** Un campo propio por su clave (para etiqueta y tope al validar). */
const campoPropio = (f: Formato, clave: string): CampoDelFormato | undefined =>
  f.secciones.flatMap((s) => s.campos).find((c) => c.editable_aqui && c.clave === clave);

// Cómo nombra el servidor cada texto libre en el aviso de tope (`CAMPOS_DE_TEXTO_LIBRE`):
// «Detalle» a secas es ambiguo entre la §7 y la §10, así que ahí el nombre es el largo.
const NOMBRE_EN_EL_AVISO: Record<string, string> = { accion_tras_evento_detalle: 'Detalle de la acción tras el evento' };
const nombreEnElAviso = (f: Formato, clave: string): string => NOMBRE_EN_EL_AVISO[clave] ?? campoPropio(f, clave)?.etiqueta ?? clave;

/** El mismo dato con dos escrituras: «Sí»/«No» del servidor y 'si'/'no' del desplegable. */
const mismoValor = (a: string, b: string): boolean => aOpcionSiNo(a) === aOpcionSiNo(b);

/** Sí/No del desplegable o del servidor a booleano; cualquier otra cosa es «sin dato». */
const booleanoDe = (valor: string): boolean | null => {
  if (valor === 'si' || valor === 'Sí') return true;
  if (valor === 'no' || valor === 'No') return false;
  return null;
};

// O-08 (QA, 28-sep-2026, OBS-2 y OBS-3a) · LOS QUE SE COMPLETAN AQUÍ Y SE GUARDAN EN EL CASO.
//
// Cuatro campos del formulario oficial que el Formato Verde no captura. Salían «—»
// rotulados «Formato Verde» y ninguna pantalla los llenaba. Los completa Asuntos
// Regulatorios aquí (AF §7c) y se guardan en el CASO por su propia puerta, como la fecha de
// término: por eso NO son `editable_aqui`, que es lo que viaja en el borrador del trámite.
//
// Va por clave base (sin índice de bloque), como `CLAVES_DE_FECHA`, y es espejo de
// `CLAVES_QUE_SE_COMPLETAN_EN_EL_CASO` (`formato_amarillo_documento.py`). Los topes son
// espejo de `casos/dominio/datos_del_formato_amarillo.py`: aquí son ayuda —maxLength y
// contador—; el que manda es el del servidor, y su 422 se lee bajo el campo.
type CampoDelCaso = 'serie' | 'via_y_lugar' | 'segun_rotulo' | 'seguimiento_cronologico';
type ControlDelCaso = 'texto' | 'parrafo' | 'si_no';
const SE_COMPLETAN_EN_EL_CASO: Record<string, { campo: CampoDelCaso; control: ControlDelCaso; tope?: number }> = {
  'medicamento.serie': { campo: 'serie', control: 'texto', tope: 50 },
  'tratamiento.via_y_lugar': { campo: 'via_y_lugar', control: 'texto', tope: 200 },
  'tratamiento.segun_rotulo': { campo: 'segun_rotulo', control: 'si_no' },
  'evento.seguimiento': { campo: 'seguimiento_cronologico', control: 'parrafo', tope: 500 },
};

/** `medicamento.2.serie` -> 2; sin índice es el bloque 1. El bloque es el `orden` del producto en el caso. */
const ordenDelBloque = (clave: string): number => {
  const partes = clave.split('.');
  return partes.length === 3 && /^\d+$/.test(partes[1]) ? Number(partes[1]) : 1;
};

/** «Serie (Medicamento 2 de 2)»: la etiqueta se repite por bloque y el nombre accesible tiene que decir cuál. */
const nombreDelCampo = (c: CampoDelFormato): string => (c.bloque ? `${c.etiqueta} (${c.bloque})` : c.etiqueta);

/** El cuerpo del PATCH con SOLO el campo tocado: lo demás del producto no se toca. */
const datosParaElCaso = (c: CampoDelFormato, campo: CampoDelCaso, valor: string): DatosDelCasoParaElFormato => {
  if (campo === 'seguimiento_cronologico') return { seguimiento_cronologico: valor.trim() || null };
  const dato = campo === 'segun_rotulo' ? booleanoDe(valor) : valor.trim() || null;
  return { medicamentos: [{ orden: ordenDelBloque(c.clave), [campo]: dato }] };
};

/**
 * O-08 · Un campo que se completa aquí y se guarda en el caso. En reposo, el valor con
 * «Completar» o «Cambiar»; al pulsarlo, su control con «Guardar en el caso» y «Cancelar»: el
 * mismo patrón que la fecha de término. El 422 del servidor (el tope) queda bajo el campo.
 */
function CampoQueSeCompletaEnElCaso({ campo, control, tope, ocupado, onGuardar }: Readonly<{
  campo: CampoDelFormato;
  control: ControlDelCaso;
  tope?: number;
  ocupado: boolean;
  /** Guarda en el caso; devuelve el error del campo, o `null` si se guardó. */
  onGuardar: (valor: string) => Promise<string | null>;
}>) {
  const [editando, setEditando] = useState(false);
  const [valor, setValor] = useState('');
  const [error, setError] = useState<string | null>(null);
  const nombre = nombreDelCampo(campo);
  const accion = campo.valor ? 'Cambiar' : 'Completar';

  if (!editando) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Valor campo={campo} />
        <Button size="sm" variant="secondary" icon={Pencil} aria-label={`${accion} ${nombre}`} disabled={ocupado}
          onClick={() => { setValor(control === 'si_no' ? aOpcionSiNo(campo.valor ?? '') : campo.valor ?? ''); setError(null); setEditando(true); }}>
          {accion}
        </Button>
      </div>
    );
  }

  const cambiar = (v: string) => { setValor(v); setError(null); };
  const guardar = async () => {
    const largo = valor.trim().length;
    if (tope && largo > tope) {
      setError(mensajeDeTopeSuperado(`«${campo.etiqueta}»`, largo, tope));
      return;
    }
    const fallo = await onGuardar(valor);
    if (fallo) setError(fallo);
    else setEditando(false);
  };
  let entrada;
  if (control === 'si_no') {
    entrada = <Select aria-label={nombre} placeholder="Sin dato" value={valor} onChange={(e) => cambiar(String(e.target.value ?? ''))} options={SI_NO} />;
  } else if (control === 'parrafo') {
    entrada = <Textarea aria-label={nombre} rows={4} value={valor} maxLength={tope} onChange={(e) => cambiar(e.target.value)} />;
  } else {
    entrada = <Input aria-label={nombre} type="text" value={valor} maxLength={tope} onChange={(e) => cambiar(e.target.value)} />;
  }
  return (
    <div className="flex flex-col gap-2">
      <Field label={undefined} className="!mb-0" error={error || undefined} contador={cuentaAtras(valor, tope)}
        hint="Lo completa Asuntos Regulatorios y se guarda en el caso.">
        {entrada}
      </Field>
      <span className="inline-flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" icon={CheckCircle2} onClick={() => { guardar(); }} disabled={ocupado}>Guardar en el caso</Button>
        <Button size="sm" variant="ghost" onClick={() => { setEditando(false); setError(null); }} disabled={ocupado}>Cancelar</Button>
      </span>
    </div>
  );
}

/** El borrador como lo espera el servidor: los Sí/No a booleano y los textos recortados (vacío = null). */
const datosDelBorrador = (borrador: Record<string, string>): DatosDelFormatoAmarillo => {
  const datos: Record<string, unknown> = {};
  for (const [clave, valor] of Object.entries(borrador)) {
    datos[clave] = CAMPOS_BOOLEANOS.has(clave) ? booleanoDe(valor) : valor.trim() || null;
  }
  return datos as DatosDelFormatoAmarillo;
};

/** El subtítulo de la cabecera: país, número, L2, autoridad, estado y versión del documento. */
function ResumenDelFormato({ formato }: Readonly<{ formato: Formato }>) {
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <Bandera cod={formato.pais} />
      <Mono className="font-semibold">{formato.numero}</Mono>
      {formato.afecta_a_personas && <L2Tag />}
      {formato.autoridad && <StatusBadge tone="info">{formato.autoridad}</StatusBadge>}
      <StatusBadge tone={formato.estado === 'completo' ? 'success' : 'neutral'}>{formato.estado === 'completo' ? 'Completo' : 'Pendiente'}</StatusBadge>
      {formato.version > 0 && <StatusBadge tone="info">v{formato.version}</StatusBadge>}
    </span>
  );
}

/** Los botones de «Generar documento» y las pistas de por qué generar está deshabilitado (C7, CP-19-01 p9). */
function AccionesDeGenerar({ ocupado, hayCambios, pendientes, puedeGenerar, version, hayCambiosDesdeLaVersion, estadoDelDocumento, onGuardar, onGenerar }: Readonly<{
  ocupado: boolean; hayCambios: boolean; pendientes: number; puedeGenerar: boolean; version: number;
  hayCambiosDesdeLaVersion: boolean; estadoDelDocumento: string | null; onGuardar: () => void; onGenerar: () => void;
}>) {
  return (
    <span className="inline-flex flex-col items-end gap-1">
      <span className="inline-flex flex-wrap gap-2">
        <Button size="sm" variant="secondary" icon={Save} onClick={onGuardar} disabled={ocupado || !hayCambios}>Guardar</Button>
        <Button size="sm" icon={FileCheck2} onClick={onGenerar} disabled={ocupado || !puedeGenerar || hayCambios || !hayCambiosDesdeLaVersion}
          title={version > 0 && !hayCambiosDesdeLaVersion ? 'Nada cambió desde la última versión' : undefined}>
          {version > 0 ? `Generar de nuevo (v${version + 1})` : 'Generar documento'}
        </Button>
      </span>
      {/* EL BOTÓN DESHABILITADO DICE POR QUÉ — LOS DOS MOTIVOS A LA VEZ SI CONCURREN.
          C12 lo fija para un motivo («La v1 refleja el estado actual del caso»), y la
          regla vale para todos. Hasta el 24-sep-2026 el aviso de campos pendientes
          llevaba `!hayCambios`, así que en cuanto se tocaba un campo el texto se
          reemplazaba por «Guarda los cambios» y la pantalla dejaba de decir QUÉ faltaba
          — que es justo la secuencia de QA: completar marcas, pulsar Generar, y ver el
          botón bloqueado sin saber por qué. Ahora cada motivo se muestra por su cuenta. */}
      {pendientes > 0 && (
        <output className="text-meta text-amber-700">Faltan {pendientes} campo(s) «A completar»: complétalos desde la lista de abajo.</output>
      )}
      {hayCambios && <span className="text-meta text-ink-soft">Guarda los cambios para poder generar</span>}
      {!hayCambios && estadoDelDocumento && (
        <output className={`text-meta ${hayCambiosDesdeLaVersion ? 'text-amber-700' : 'text-ink-soft'}`}>{estadoDelDocumento}</output>
      )}
    </span>
  );
}

/** Qué botón «Ver e imprimir» está descargando: la versión y, con varias autoridades, cuál. */
const claveDeImpresion = (version: number, autoridad?: string): string => (autoridad ? `${version}:${autoridad}` : String(version));

/**
 * La lista «Versiones del Formato Amarillo» con su «Ver e imprimir» (HU-19-C4, CC2-19-C1).
 *
 * BOTÓN DEL CATÁLOGO, NO ENLACE (BUG-1090, `ui/MG-ILE-1090.md`). Era un `<a href>` a la API con
 * `target="_blank"`: la navegación de la pestaña nueva no lleva el token y con el inicio de sesión
 * de Microsoft la API respondía 401. Ahora el documento se pide por el cliente y se abre en otra
 * pestaña con `abrirDocumentoEnPestana`; el error se lee aquí, encima de la lista. Se llama «Ver e
 * imprimir» porque eso es lo que hace (QA, 24-sep-2026): abre el documento solo, y de ahí se
 * imprime con el navegador.
 */
function VersionesDelFormato({ casoId, versiones, porAutoridad, zona }: Readonly<{
  casoId: string;
  versiones: VersionDelFormato[];
  /** CC-02 · RN-79: con más de una autoridad destino, un botón por autoridad con su caja de la AC. */
  porAutoridad: { codigo: string; nombre: string }[];
  zona: string | null;
}>) {
  // Un conjunto y no una sola clave: cada botón se bloquea solo mientras descarga lo suyo.
  const [imprimiendo, setImprimiendo] = useState<ReadonlySet<string>>(() => new Set());
  const [fallo, setFallo] = useState<Fallo | null>(null);

  const marcar = (clave: string, enCurso: boolean) => setImprimiendo((actual) => {
    const siguiente = new Set(actual);
    if (enCurso) siguiente.add(clave);
    else siguiente.delete(clave);
    return siguiente;
  });

  /** El HTML de impresión de la versión: con la caja de la AC de `autoridad`, o el de siempre. */
  const descargarDe = (version: number, autoridad?: string): Promise<Blob> => (autoridad
    ? imprimirFormatoAmarilloParaLaAutoridad(casoId, version, autoridad)
    : imprimirFormatoAmarillo(casoId, version));

  // Sin `await` antes de `abrirDocumentoEnPestana`: la pestaña tiene que abrirse en el clic.
  const imprimir = async (version: number, autoridad?: string) => {
    const clave = claveDeImpresion(version, autoridad);
    if (imprimiendo.has(clave)) return;
    setFallo(null);
    try {
      await abrirDocumentoEnPestana(() => {
        marcar(clave, true);
        return descargarDe(version, autoridad);
      });
    } catch (error) {
      setFallo(aFallo(error));
    } finally {
      marcar(clave, false);
    }
  };

  return (
    <>
      {fallo && (
        <div className="mb-3">
          <Alert tone="danger" title="No se pudo abrir el Formato Amarillo." detalleTecnico={fallo.detalleTecnico}
            onCerrar={() => setFallo(null)} data-testid="formato-imprimir-error">
            {fallo.message}
          </Alert>
        </div>
      )}
      <ul className="divide-y divide-hairline" aria-label="Versiones del Formato Amarillo">
        {versiones.map((v) => (
          <li key={v.version} className="py-2 flex flex-wrap items-center gap-3 text-meta">
            <Mono className="font-semibold">Formato Amarillo v{v.version}</Mono>
            <span className="text-ink-soft">{comoFechaYHora(v.generado_en, zona)}</span>
            {v.generado_por && <span className="text-ink-soft" title={v.generado_por}>usuario {v.generado_por.slice(0, 8)}</span>}
            {porAutoridad.length > 1 ? (
              // Un solo formato, con la caja de la AC de cada autoridad destino. Con una sola, el
              // botón de siempre (la predeterminada).
              <span className="ml-auto inline-flex flex-wrap justify-end gap-2">
                {porAutoridad.map((a) => {
                  const clave = claveDeImpresion(v.version, a.codigo);
                  return (
                    <BotonVerEImprimir key={a.codigo} ocupado={imprimiendo.has(clave)} texto={`Ver e imprimir v${v.version} · ${a.nombre}`}
                      onImprimir={() => { void imprimir(v.version, a.codigo); }}
                      aria-label={`Ver e imprimir el Formato Amarillo v${v.version} para ${a.nombre}`}
                      data-testid="formato-imprimir" data-autoridad={a.codigo} data-version={v.version} />
                  );
                })}
              </span>
            ) : (
              <BotonVerEImprimir className="ml-auto" ocupado={imprimiendo.has(claveDeImpresion(v.version))} texto={`Ver e imprimir v${v.version}`}
                onImprimir={() => { void imprimir(v.version); }}
                data-testid="formato-imprimir-version" data-version={v.version} />
            )}
          </li>
        ))}
      </ul>
    </>
  );
}

export default function FormatoAmarillo({ casoId, onVolver, onAbrirCaso, textoDeVolver = 'Volver a la bandeja', puedeCompletarEnElCaso = true }: {
  casoId: string;
  onVolver: () => void;
  /** Abre el expediente (Formato Verde): donde se corrige lo que aquí sale «A completar» con origen Formato Verde. */
  onAbrirCaso?: (casoId: string) => void;
  /** «Volver al trámite» cuando se llegó desde él; «Volver a la bandeja» cuando se abrió directo. */
  textoDeVolver?: string;
  /**
   * Si el rol escribe en el CASO lo que esta pantalla pide y el Verde no captura (la fecha de
   * término y los campos de O-08). El servidor lo admite a Asuntos Regulatorios y al
   * administrador (`SOLO_QUIEN_NOTIFICA`); el Representante del País ve el documento pero
   * recibiría 403, así que no se le ofrece el control.
   */
  puedeCompletarEnElCaso?: boolean;
}) {
  const [formato, setFormato] = useState<Formato | null>(null);
  const [fallo, setFallo] = useState<Fallo | null>(null);
  const zona = useZonaDelPais()(formato?.pais);
  const [borrador, setBorrador] = useState<Record<string, string>>({});
  // Lo escrito y no guardado que supera su tope, por clave (QA, 21-sep-2026).
  const [erroresDeTexto, setErroresDeTexto] = useState<Record<string, string>>({});
  // QA 21-sep-2026: la regla de negocio de la fecha (422, «no puede ser anterior a la de
  // inicio») se lee bajo el campo, como cualquier otro error de campo; arriba solo van
  // los fallos que no son de un campo.
  const [errorFechaTermino, setErrorFechaTermino] = useState<string | null>(null);
  const [fechaTermino, setFechaTermino] = useState('');
  // C6 (22-sep-2026, CP-19-01 p7/p11): la fecha ya fijada se puede corregir mientras el
  // Amarillo sea editable; el caso la admite (solo exige que no sea anterior al inicio).
  const [cambiandoFecha, setCambiandoFecha] = useState(false);
  const [catalogos, setCatalogos] = useState<Record<string, Opcion[]>>({});
  // Los catálogos que no cargaron: un desplegable vacío sin explicación parecía un campo roto.
  const [catalogosCaidos, setCatalogosCaidos] = useState<string[]>([]);
  const [ocupado, setOcupado] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // HU-20-C5: generar de nuevo con una carta ya preparada la deja desactualizada; se confirma.
  const [confirmandoGenerar, setConfirmandoGenerar] = useState(false);
  // El documento con el que se armó el borrador actual: es contra lo que se sabe qué
  // cambió el analista sin guardar (ver `cargar`).
  const formatoDelBorrador = useRef<Formato | null>(null);

  /**
   * Trae el documento del servidor y arma el borrador de los campos propios.
   *
   * `conservarCambios` (QA, 21-sep-2026): «Fijar en el caso» recargaba el documento y el
   * borrador se rearmaba desde el servidor, así que lo escrito y no guardado en las demás
   * secciones —tipo de informe, referencia, notificante, acción, detalle…— se perdía sin
   * aviso. Con la bandera, lo que difiere de lo que había en pantalla se conserva y solo
   * se refresca lo que el analista no tocó.
   *
   * Solo cuenta la lectura VIGENTE: cada lectura toma un turno y el desmontaje (también el que
   * StrictMode simula en desarrollo) invalida los anteriores. Una lectura vieja que llegaba
   * después de teclear rearmaba el borrador desde el servidor y lo escrito se perdía (HU-20-C5/C6
   * en la E2E `03_regulatorio_peru`).
   */
  const lecturaVigente = useRef(0);
  const cargar = useCallback(async (conservarCambios = false) => {
    const turno = ++lecturaVigente.current;
    setFallo(null);
    try {
      const f = await verFormatoAmarillo(casoId);
      if (turno !== lecturaVigente.current) return;
      const propios = propiosDe(f);
      const anterior = formatoDelBorrador.current;
      setFormato(f);
      setBorrador((actual) => {
        if (!conservarCambios || !anterior) return propios;
        const previos = propiosDe(anterior);
        const conservado = { ...propios };
        for (const [clave, valor] of Object.entries(actual)) {
          if (clave in previos && !mismoValor(valor, previos[clave])) conservado[clave] = valor;
        }
        return conservado;
      });
      formatoDelBorrador.current = f;
    } catch (e) {
      if (turno !== lecturaVigente.current) return;
      setFallo(aFallo(e));
    }
  }, [casoId]);

  // `cargar` ya captura sus fallos y los deja en `fallo`: no hay promesa suelta que vigilar.
  useEffect(() => {
    cargar();
    return () => { lecturaVigente.current += 1; };
  }, [cargar]);
  useEffect(() => {
    let vivo = true;
    for (const [clave, nombre] of Object.entries(CATALOGO_DE)) {
      listarCatalogo(nombre)
        .then((r) => { if (vivo) setCatalogos((c) => ({ ...c, [clave]: opcionesDelCatalogo(r.valores) })); })
        .catch(() => { if (vivo) setCatalogosCaidos((x) => (x.includes(nombre) ? x : [...x, nombre])); });
    }
    return () => { vivo = false; };
  }, []);

  const hayCambios = useMemo(() => {
    if (!formato) return false;
    // Sí/No se compara normalizado: re-elegir el mismo valor no es un cambio.
    for (const s of formato.secciones) for (const c of s.campos) if (c.editable_aqui && !mismoValor(borrador[c.clave] ?? '', c.valor ?? '')) return true;
    return false;
  }, [formato, borrador]);

  /** Lo escrito que supera su tope, por clave; vacío si todo cabe. */
  const excesosDeTexto = (): Record<string, string> => {
    const excesos: Record<string, string> = {};
    if (!formato) return excesos;
    for (const [clave, valor] of Object.entries(borrador)) {
      const tope = topeDelFormato(formato, clave);
      const largo = valor.trim().length;
      if (tope && largo > tope) excesos[clave] = mensajeDeTopeSuperado(`«${nombreEnElAviso(formato, clave)}»`, largo, tope);
    }
    return excesos;
  };

  /** El 422 del servidor por largo («… supera el máximo de N caracteres») va bajo su campo. */
  const bajoSuCampo = (f: Fallo): boolean => {
    if (!formato || f.estado !== 422 || !/supera el máximo de \d+ caracteres/.test(f.message)) return false;
    const clave = Object.keys(borrador).find((k) => f.message.includes(`«${nombreEnElAviso(formato, k)}»`));
    if (!clave) return false;
    setErroresDeTexto((x) => ({ ...x, [clave]: f.message }));
    return true;
  };

  const guardar = async () => {
    if (!formato) return;
    setFallo(null);
    setAviso(null);
    const excesos = excesosDeTexto();
    setErroresDeTexto(excesos);
    if (Object.keys(excesos).length > 0) return;
    setOcupado(true);
    try {
      await guardarFormatoAmarillo(casoId, datosDelBorrador(borrador));
      await cargar();
      setAviso('Formato Amarillo guardado.');
    } catch (e) {
      const f = aFallo(e);
      if (!bajoSuCampo(f)) setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  const fijarFecha = async () => {
    if (!fechaTermino) return;
    setFallo(null);
    setErrorFechaTermino(null);
    setOcupado(true);
    try {
      await fijarFechaDeTerminoDelEvento(casoId, fechaTermino);
      // La fecha vive en el caso; lo escrito aquí y no guardado se conserva (QA, 21-sep-2026).
      await cargar(true);
      setFechaTermino('');
      setCambiandoFecha(false);
      setAviso(hayCambios
        ? 'Fecha de término guardada. Pulsa «Guardar» para conservar lo demás que escribiste.'
        : 'Fecha de término guardada.');
    } catch (e) {
      const f = aFallo(e);
      if (f.estado === 422) setErrorFechaTermino(f.message);
      else setFallo(f);
    } finally {
      setOcupado(false);
    }
  };

  /**
   * O-08: guarda en el caso uno de los campos que el Verde no captura y recarga el documento
   * conservando lo escrito y no guardado en las demás secciones (como «Fijar en el caso»).
   * Devuelve el error para el campo (el 422: tope, producto inexistente) o `null`; lo que no
   * es del campo va arriba.
   */
  const completarEnElCaso = async (c: CampoDelFormato, campo: CampoDelCaso, valor: string): Promise<string | null> => {
    setFallo(null);
    setAviso(null);
    setOcupado(true);
    try {
      await completarDatosDelFormatoAmarillo(casoId, datosParaElCaso(c, campo, valor));
      await cargar(true);
      setAviso(hayCambios
        ? `«${c.etiqueta}» guardado. Pulsa «Guardar» para conservar lo demás que escribiste.`
        : `«${c.etiqueta}» guardado.`);
      return null;
    } catch (e) {
      const f = aFallo(e);
      if (f.estado === 422) return f.message;
      setFallo(f);
      return null;
    } finally {
      setOcupado(false);
    }
  };

  const generar = async () => {
    setConfirmandoGenerar(false);
    setFallo(null);
    setAviso(null);
    setOcupado(true);
    try {
      const f = await generarFormatoAmarillo(casoId);
      setFormato(f);
      // Generar exige el borrador guardado: el documento nuevo es la referencia del borrador.
      formatoDelBorrador.current = f;
      setAviso(`Formato Amarillo v${f.version} generado.`);
    } catch (e) {
      setFallo(aFallo(e));
    } finally {
      setOcupado(false);
    }
  };

  const cabecera = (
    <PageHeader
      eyebrow="Asuntos Regulatorios · Formato Amarillo"
      hu="HU-19 · RN-33 / RN-08 / RN-09 / RN-14 / RN-15 · Dato L2"
      title="Formato Amarillo"
      subtitle={formato ? <ResumenDelFormato formato={formato} /> : undefined}
      icon={FileCheck2}
      actions={<Button variant="secondary" size="sm" icon={ArrowLeft} onClick={onVolver}>{textoDeVolver}</Button>}
    />
  );

  if (fallo && !formato) {
    return <>{cabecera}<FalloAlAbrir fallo={fallo} /></>;
  }
  if (!formato) return <>{cabecera}<p className="p-6 text-body text-ink-soft">Abriendo el Formato Amarillo…</p></>;

  const pendientes = formato.campos_por_completar.length;
  // CC-02 (B11): las autoridades de las cartas activas, para imprimir con el encabezado de cada una.
  const porAutoridad = formato.autoridades_destino ?? [];
  // 21-sep-2026: con la carta firmada o enviada el Amarillo se congela (el servidor dice por qué).
  const editable = formato.puede_editar !== false;
  // «Generar de nuevo» solo cuando lo que se ve difiere de la última versión generada;
  // la primera generación no tiene con qué compararse.
  const hayCambiosDesdeLaVersion = formato.version === 0 || formato.hay_cambios_desde_la_ultima_version === true;
  // Hay una carta que se apoya en el Amarillo actual: generar otro la deja desactualizada (HU-20-C5).
  const hayCartaQueQuedariaAtras = (formato.carta_version ?? 0) > 0 && formato.estado_carta !== 'enviada';
  const textoDelEstadoDelDocumento = (): string | null => {
    if (formato.version === 0) return null;
    if (hayCambiosDesdeLaVersion) return `La v${formato.version} ya no refleja el caso: cambió ${(formato.campos_cambiados ?? []).join(', ') || 'algún dato'}.`;
    return `La v${formato.version} refleja el estado actual del caso.`;
  };
  const generarOConfirmar = () => {
    if (hayCartaQueQuedariaAtras) setConfirmandoGenerar(true);
    else generar();
  };
  // Los «A completar» con su etiqueta y de dónde vienen, para la lista de la tarjeta.
  const camposPendientes = formato.secciones.flatMap((s) => s.campos.filter((c) => c.a_completar).map((c) => ({ ...c, seccion: s.numero })));
  // C7: la fila del campo lleva `id="campo-<clave>"`; se desplaza hasta ella y se enfoca su control.
  const irAlCampo = (clave: string) => {
    const fila = document.getElementById(`campo-${clave}`);
    if (!fila) return;
    fila.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    const control = fila.querySelector<HTMLElement>('input, textarea, select, [role="combobox"], button');
    control?.focus();
  };

  const cambiarBorrador = (clave: string, valor: string) => {
    setBorrador((b) => ({ ...b, [clave]: valor }));
    // El aviso de tope se retira en cuanto se corrige el campo (como en Acciones y Causalidad).
    setErroresDeTexto((x) => (x[clave] ? { ...x, [clave]: '' } : x));
  };

  // La celda del valor, según qué es el campo: propio del trámite (se edita aquí, C5), la
  // fecha de término (se pide aquí y se fija en el caso, C3) o un dato que se corrige en
  // otra pantalla, con la pista de cómo resolverlo si sale «A completar».
  const celdaDelCampo = (c: CampoDelFormato) => {
    if (c.editable_aqui && !editable) {
      // Congelado: se ve lo guardado, sin control (el motivo está en el aviso de arriba);
      // un código de catálogo se muestra con su nombre, como en el desplegable.
      const nombre = (catalogos[c.clave] ?? []).find((o) => o.value === c.valor)?.label;
      return <Valor campo={nombre ? { ...c, valor: nombre } : c} />;
    }
    if (c.editable_aqui) {
      const tope = topeDelFormato(formato, c.clave);
      const valor = borrador[c.clave] ?? '';
      return (
        <Field label={undefined} className="!mb-0" error={erroresDeTexto[c.clave] || undefined} contador={cuentaAtras(valor, tope)}>
          <EntradaPropia campo={c} valor={valor} opciones={catalogos[c.clave] ?? []} tope={tope} onCambiar={(v) => cambiarBorrador(c.clave, v)} />
        </Field>
      );
    }
    // Lo que se escribe en el CASO: solo con el Amarillo editable (HU-19-C13) y para el rol
    // que el servidor admite en esa puerta.
    const escribeEnElCaso = editable && puedeCompletarEnElCaso;
    const enElCaso = SE_COMPLETAN_EN_EL_CASO[claveBase(c.clave)];
    if (enElCaso && escribeEnElCaso) {
      return (
        <CampoQueSeCompletaEnElCaso campo={c} control={enElCaso.control} tope={enElCaso.tope} ocupado={ocupado}
          onGuardar={(v) => completarEnElCaso(c, enElCaso.campo, v)} />
      );
    }
    if (c.clave === CLAVE_FECHA_TERMINO && escribeEnElCaso) return celdaDeLaFechaDeTermino(c);
    return celdaDeConsulta(c);
  };

  // C3/C6: la fecha de término se pide aquí y se fija en el caso; ya fijada, se ofrece corregirla.
  const celdaDeLaFechaDeTermino = (c: CampoDelFormato) => {
    if (!c.a_completar && !cambiandoFecha) {
      // C6: ya fijada. Se ve el valor y se ofrece corregirla (el CP-19-01 lo exige en su paso 11).
      return (
        <div className="flex flex-wrap items-center gap-2">
          <Valor campo={c} />
          <Button size="sm" variant="secondary" icon={Pencil} onClick={() => { setFechaTermino(String(c.valor ?? '').slice(0, 10)); setCambiandoFecha(true); }} disabled={ocupado}>Cambiar</Button>
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Valor campo={c} />
        <Input aria-label="Fecha de término del evento" type="date" value={fechaTermino} aria-invalid={errorFechaTermino ? true : undefined}
          aria-describedby={errorFechaTermino ? 'error-fecha-termino' : undefined}
          onChange={(e) => { setFechaTermino(e.target.value); setErrorFechaTermino(null); }} className="!w-44 max-w-full" />
        <Button size="sm" variant="secondary" icon={CheckCircle2} onClick={() => { fijarFecha(); }} disabled={ocupado || !fechaTermino}>Fijar en el caso</Button>
        {cambiandoFecha && (
          <Button size="sm" variant="ghost" onClick={() => { setCambiandoFecha(false); setFechaTermino(''); setErrorFechaTermino(null); }} disabled={ocupado}>Cancelar</Button>
        )}
        {errorFechaTermino ? (
          <p id="error-fecha-termino" role="alert" className="basis-full text-meta text-rose-700 flex items-start gap-1 break-words">
            <AlertTriangle size={12} className="shrink-0 mt-0.5" /> <span>{errorFechaTermino}</span>
          </p>
        ) : (
          <span className="basis-full text-meta text-ink-soft">Se guarda en el caso (sección 8).</span>
        )}
      </div>
    );
  };

  // Un dato que se corrige en otra pantalla, con la pista de cómo resolverlo si sale «A completar».
  const celdaDeConsulta = (c: CampoDelFormato) => (
    <div className="flex flex-wrap items-center gap-2">
      <Valor campo={c} />
      {/* Junto a cada «A completar», cómo resolverlo según de dónde viene el dato. */}
      {c.a_completar && c.origen === 'formato_verde' && onAbrirCaso && (
        <Button size="sm" variant="secondary" icon={FolderOpen} onClick={() => onAbrirCaso(casoId)}>Ver expediente</Button>
      )}
      {c.a_completar && c.origen === 'evaluacion' && (
        <span className="text-meta text-ink-soft">Lo registra el Comité en la pestaña Causalidad</span>
      )}
      {c.a_completar && c.detalle && <span className="basis-full text-meta text-ink-soft mt-0.5">{c.detalle}</span>}
    </div>
  );

  return (
    <>
      {cabecera}
      {fallo && <div className="mb-3"><Alert tone="danger" title="No se pudo completar" detalleTecnico={fallo.detalleTecnico}>{fallo.message}</Alert></div>}
      {aviso && <div className="mb-3"><Alert tone="success" title={aviso} onCerrar={() => setAviso(null)} /></div>}
      {catalogosCaidos.length > 0 && (
        <div className="mb-3">
          <Alert tone="warning" title="No se pudieron cargar los catálogos de los desplegables">
            {catalogosCaidos.join(', ')}. Los desplegables afectados aparecen vacíos: recarga la página; si persiste, avisa a Administración.
          </Alert>
        </div>
      )}

      {!editable && formato.motivo_de_no_editable && (
        <div className="mb-3">
          <Alert tone="info" title="El Formato Amarillo quedó congelado" icon={Lock}>{formato.motivo_de_no_editable}</Alert>
        </div>
      )}

      <Card className="mb-4" title="Generar documento" icon={FileCheck2}
        subtitle={pendientes === 0 ? 'Todas las marcas «A completar» están resueltas.' : `${pendientes} campo(s) «A completar». Complétalos para generar el documento.`}
        actions={editable ? (
          <AccionesDeGenerar ocupado={ocupado} hayCambios={hayCambios} pendientes={pendientes} puedeGenerar={formato.puede_generar} version={formato.version}
            hayCambiosDesdeLaVersion={hayCambiosDesdeLaVersion} estadoDelDocumento={textoDelEstadoDelDocumento()} onGuardar={() => { guardar(); }} onGenerar={generarOConfirmar} />
        ) : undefined}>
        {hayCartaQueQuedariaAtras && editable && (
          <p className="mb-3 text-meta text-ink-soft" role="note">
            Este trámite ya tiene la carta v{formato.carta_version} ({etiquetaDeLaCarta(formato.estado_carta).toLowerCase()}), hecha con el Formato Amarillo v{formato.formato_amarillo_version_de_la_carta ?? '—'}: si generas el v{formato.version + 1}, la carta quedará desactualizada y habrá que regenerarla antes de firmarla.
          </p>
        )}
        {camposPendientes.length > 0 && (
          <ul className="mb-3 space-y-1" aria-label="Campos por completar">
            {camposPendientes.map((c) => (
              <li key={c.clave} className="flex flex-wrap items-center gap-2 text-body">
                <AlertTriangle size={13} className="text-amber-600 shrink-0" aria-hidden="true" />
                {/* C7: cada pendiente lleva a su campo (desplaza la sección y enfoca el control si lo hay). */}
                {/* `min-h-6`: 24 px de objetivo (WCAG 2.5.8); con la altura de la línea quedaba en 20. */}
                <button type="button" className="min-h-6 text-left underline decoration-dotted underline-offset-2 hover:text-marca-texto" onClick={() => irAlCampo(c.clave)}>
                  Sección {c.seccion} · {c.etiqueta}
                </button>
                <span className="text-meta text-ink-soft">{ORIGEN[c.origen] ?? c.origen}</span>
              </li>
            ))}
          </ul>
        )}
        {formato.versiones.length === 0
          ? <p className="text-meta text-ink-soft">Todavía no se ha generado ninguna versión.</p>
          : (
            <VersionesDelFormato casoId={casoId} versiones={formato.versiones} porAutoridad={porAutoridad} zona={zona} />
          )}
      </Card>

      {formato.afecta_a_personas && (
        <div className="mb-3">
          <Alert tone="info" title="Sección 11 · dato L2" icon={Lock}>
            Este caso afecta a personas. La lectura del documento queda registrada en la auditoría.
          </Alert>
        </div>
      )}

      <div className="space-y-3">
        {formato.secciones.map((s) => (
          <Card key={s.numero} pad={false} title={`${s.numero}. ${s.titulo}`} className={s.campos.some((c) => c.a_completar) ? 'border-amber-300' : ''}>
            <section aria-label={`${s.numero}. ${s.titulo}`}>
              {/* `table-fixed` y corte de palabra: a 320 px la columna del valor no puede crecer mas
                  que la tarjeta (WCAG 1.4.10, auditoria AA del 2-oct-2026). */}
              <table className="w-full table-fixed text-body [overflow-wrap:anywhere]">
                <tbody>
{/* Las secciones 4, 5 y 6 admiten varios bloques (dos lotes de animales, dos
                      productos). La sección NO se duplica —el Formato Verde tampoco lo hace—:
                      se rotula por dentro con «Animal 1 de 2». Sin esta cabecera la tabla
                      mostraría «Especie / Especie / Raza / Raza» sin decir de cuál habla cada
                      una. Un campo sin `bloque` se pinta igual que siempre. */}
                  {s.campos.map((c, i) => (
                    <Fragment key={c.clave}>
                      {c.bloque && c.bloque !== s.campos[i - 1]?.bloque && (
                        <tr className="border-t border-hairline bg-canvas">
                          <th scope="colgroup" colSpan={2} className="text-left px-4 py-1.5 text-meta font-semibold text-ink-soft">
                            {c.bloque}
                          </th>
                        </tr>
                      )}
                      <tr id={`campo-${c.clave}`} className="border-t border-hairline">
                        <th scope="row" className="text-left font-medium text-meta px-4 max-sm:px-3 py-2 w-2/5 sm:w-1/3 align-top">
                          {c.etiqueta}
                          <span className="block text-meta font-normal text-ink-soft">{ORIGEN[c.origen] ?? c.origen}</span>
                        </th>
                        <td className="px-4 max-sm:px-3 py-2 align-top">{celdaDelCampo(c)}</td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </section>
          </Card>
        ))}
      </div>
      <p className="mt-3 text-meta text-ink-soft inline-flex items-center gap-1"><History size={12} /> Última generación: {formato.versiones.length ? fecha(formato.versiones[formato.versiones.length - 1].generado_en, zona) : 'ninguna'}</p>
      <Modal open={confirmandoGenerar} onClose={() => setConfirmandoGenerar(false)} tone="warning" icon={AlertTriangle}
        eyebrow="Formato Amarillo · versión nueva" title={`¿Generar el Formato Amarillo v${formato.version + 1}?`}
        footer={<>
          <Button variant="secondary" onClick={() => setConfirmandoGenerar(false)}>Cancelar</Button>
          <Button icon={FileCheck2} onClick={() => { generar(); }} disabled={ocupado}>Generar de nuevo</Button>
        </>}>
        <p className="text-body">
          La carta v{formato.carta_version} de este trámite se hizo con el Formato Amarillo v{formato.formato_amarillo_version_de_la_carta ?? '—'}. Al generar el v{formato.version + 1} la carta quedará desactualizada: habrá que regenerarla antes de pedir o registrar la firma, y una solicitud de firma ya enviada queda sin efecto.
        </p>
      </Modal>
    </>
  );
}
